/**
 * Iter 200a · SearchableSelect
 *
 * Drop-in Shadcn-styled dropdown that auto-sorts options and offers
 * an inline search filter. Designed for data-driven pickers where
 * plain <Select> becomes cumbersome (industries, brands, clients,
 * templates, etc.).
 *
 * Options can be either strings (auto {value, label}) or
 * {value, label, hint?, disabled?} objects.
 */
import { useMemo, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check, ChevronsUpDown, Search as SearchIcon, X } from "lucide-react";

function normalise(opts) {
  return (opts || []).map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );
}

export default function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  clearable = false,
  sort = "asc",
  className = "",
  triggerClassName = "",
  contentClassName = "",
  disabled = false,
  emptyText = "No matches",
  "data-testid": dataTestid,
  testid: testidProp,
  width = 220,
}) {
  const testid = dataTestid || testidProp;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const items = useMemo(() => {
    let arr = normalise(options);
    if (sort === "asc") {
      arr = [...arr].sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" })
      );
    }
    const t = q.trim().toLowerCase();
    if (!t) return arr;
    return arr.filter((o) =>
      String(o.label).toLowerCase().includes(t) ||
      String(o.value).toLowerCase().includes(t) ||
      (o.hint || "").toLowerCase().includes(t)
    );
  }, [options, q, sort]);

  const current = normalise(options).find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={`inline-flex items-center gap-2 h-9 border border-border bg-card px-2 text-sm text-left disabled:opacity-60 ${triggerClassName}`}
          style={{ width }}
          data-testid={testid}
        >
          <span className={`truncate flex-1 ${current ? "" : "text-muted-foreground"}`}>
            {current ? current.label : placeholder}
          </span>
          {clearable && current && !disabled && (
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); onValueChange?.(""); }}
              className="hover:text-destructive"
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={`p-0 rounded-none w-64 z-[1300] ${contentClassName} ${className}`}>
        <div className="p-2 border-b border-border flex items-center gap-1.5">
          <SearchIcon className="w-3.5 h-3.5 text-muted-foreground" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="h-8 rounded-none text-sm border-0 shadow-none focus-visible:ring-0 px-0"
            data-testid={testid ? `${testid}-search` : undefined}
          />
        </div>
        <div className="max-h-72 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground italic text-center">{emptyText}</div>
          ) : (
            items.map((o) => (
              <button
                type="button"
                key={o.value}
                disabled={o.disabled}
                onClick={() => { onValueChange?.(o.value); setOpen(false); setQ(""); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left hover:bg-muted/40 disabled:opacity-50 ${o.value === value ? "bg-primary/10 text-primary" : ""}`}
                data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
              >
                <span className="w-4 shrink-0">{o.value === value && <Check className="w-3.5 h-3.5" />}</span>
                <span className="flex-1 truncate">{o.label}</span>
                {o.hint && <span className="text-[10px] text-muted-foreground truncate">{o.hint}</span>}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
